import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import "./WaveNotify.css";
import SEO from "../components/SEO";
import MagneticButton from "../components/MagneticButton";

const features = [
  {
    icon: "🛎️",
    title: "Automatic Notifications",
    desc: "Automatically send email notifications to customers when products come back in stock. No manual work needed.",
  },
  {
    icon: "📊",
    title: "Advanced Analytics",
    desc: "Track notification usage, subscriber interest, and performance metrics with detailed analytics dashboard.",
  },
  {
    icon: "🛒",
    title: "Shopify Integration",
    desc: "100% integrated with your Shopify inventory. One-click install, zero configuration required.",
  },
  {
    icon: "🎨",
    title: "Form Customizer",
    desc: "Customize the appearance of notification forms with colors, fonts, and styling options (Starter+ plans).",
  },
  {
    icon: "👥",
    title: "Customer Account Creation",
    desc: "Automatically create customer accounts in Shopify from subscribers with custom tags and email marketing opt-in.",
  },
];

const steps = [
  {
    step: "Step 1",
    title: "Install Wave Notify",
    desc: "Find us on the Shopify App Store and click 'Add App'. We'll guide you through the rest.",
  },
  {
    step: "Step 2",
    title: "Automatic Form Display",
    desc: "Notification forms automatically appear on product pages when items are out of stock.",
  },
  {
    step: "Step 3",
    title: "Capture Sales",
    desc: "Customers subscribe, you restock, we automatically notify them. Recover sales from out-of-stock products.",
  },
];

const pricingPlans = [
  {
    name: "Free Plan",
    price: "Free",
    features: [
      "Starter quota of notifications per month",
      "Automatic form display on sold-out products",
      "Basic analytics",
    ],
  },
  {
    name: "Paid Plans",
    price: "Tiered",
    features: [
      "Higher notification limits",
      "Additional customization options",
      "Expanded analytics and ongoing improvements",
    ],
  },
];

function WaveNotify() {
  useEffect(() => {
    document.body.setAttribute("data-page", "wavenotify");
    return () => document.body.removeAttribute("data-page");
  }, []);

  return (
    <>
      <SEO />

      <motion.div
        className="wavenotify-wrapper"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <motion.section
          className="wavenotify-hero"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="hero-bg-glow" />

          <div className="hero-content">
            <h1>Wave Notify, Restock Infrastructure for Shopify</h1>
            <h2 className="hero-subtitle">
              Capture sales from out-of-stock products with automated email notifications when inventory is restored.
            </h2>
          </div>

          <div className="video-container">
            <iframe
              className="hero-video"
              width="1120"
              height="630"
              src="https://www.youtube.com/embed/7YdF1Crijc0?autoplay=1&mute=1&rel=0&modestbranding=1&playsinline=1"
              title="Wave Notify demo"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>

          <MagneticButton
            href="https://apps.shopify.com/wave-notify"
            className="cta-button insane pulse"
            aria-label="Add Wave Notify on the Shopify App Store"
          >
            Add to Shopify
          </MagneticButton>
        </motion.section>

        <section className="wavenotify-features">
          <h2 className="section-title">Why Merchants Love Wave Notify</h2>
          <div className="feature-grid">
            {features.map((feat, idx) => (
              <motion.div
                className="feature-card"
                key={idx}
                initial={{ opacity: 0, y: 40, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ 
                  delay: idx * 0.1, 
                  duration: 0.6,
                  type: "spring",
                  stiffness: 100
                }}
                whileHover={{ 
                  y: -5,
                  scale: 1.02,
                  transition: { duration: 0.3 }
                }}
              >
                <motion.div 
                  className="feature-icon"
                  animate={{
                    scale: [1, 1.1, 1],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: idx * 0.3
                  }}
                >
                  {feat.icon}
                </motion.div>
                <h3>{feat.title}</h3>
                <p>{feat.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="wavenotify-steps">
          <h2 className="section-title">How It Works</h2>
          <div className="steps-wrapper">
            {steps.map((step, i) => (
              <motion.div
                key={i}
                className="step-card"
                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ 
                  delay: i * 0.2,
                  duration: 0.6,
                  type: "spring",
                  stiffness: 100
                }}
                whileHover={{ 
                  y: -5,
                  scale: 1.02,
                  transition: { duration: 0.2 }
                }}
              >
                <motion.h4
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ 
                    delay: i * 0.2 + 0.3,
                    type: "spring",
                    stiffness: 200
                  }}
                >
                  {step.step}
                </motion.h4>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="wavenotify-use-cases">
          <h2 className="section-title">Perfect For</h2>
          <div className="use-cases-grid">
            {[
              "Seasonal products with limited stock",
              "High-demand items that sell out quickly",
              "Pre-order and coming soon products",
              "Stores with fluctuating inventory"
            ].map((useCase, idx) => (
              <motion.div
                key={idx}
                className="use-case-card"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1, duration: 0.5 }}
                whileHover={{ x: 5, transition: { duration: 0.2 } }}
              >
                <span className="use-case-icon">✓</span>
                <p>{useCase}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* 💰 PRICING SECTION */}
        <section className="wavenotify-pricing">
          <h2 className="section-title">Simple, Transparent Pricing</h2>
          <div className="pricing-grid">
            {pricingPlans.map((plan, idx) => (
              <motion.div
                key={idx}
                className={`pricing-card ${plan.popular ? "popular" : ""}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.15 }}
              >
                {plan.popular && <div className="popular-badge">Most Popular</div>}
                <h3>{plan.name}</h3>
                <div className="price">
                  <span className="price-amount">{plan.price}</span>
                  {plan.priceYearly && (
                    <span className="price-yearly">{plan.priceYearly}</span>
                  )}
                </div>
                <ul className="features-list">
                  {plan.features.map((feature, fIdx) => (
                    <li key={fIdx}>✓ {feature}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </section>

        {/* 🔥 FINAL CTA */}
        <motion.section
          className="wavenotify-cta"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="cta-box">
            <h2><span className="text-gradient">Ready to Recover Sales?</span></h2>
            <p>Start using Wave Notify today. It's free to install and works instantly with Shopify.</p>
            <MagneticButton
              href="https://apps.shopify.com/wave-notify"
              className="cta-button insane pulse"
              aria-label="Install Wave Notify from the Shopify App Store"
            >
              Install Wave Notify Free
            </MagneticButton>
          </div>
        </motion.section>
      </motion.div>
    </>
  );
}

export default WaveNotify;



